import axios from 'axios';
import { env } from '@/config/env';
import { authSession, useAuthStore } from '@/features/auth/stores/auth.store';
import { hostOf } from '@/features/auth/api/auth.mappers';
import { authApi } from '@/lib/api/client';
import { refreshTokens } from '@/lib/api/tokenRefresh';
import { endpoints } from '@/lib/api/endpoints';

/**
 * Lazy per-host EasyAuth (`X-ZUMO-AUTH`) tokens for MEDIS /v2.
 *
 * A token is only valid on the host that issued it. The auth server co-issues
 * ONE with the OAuth tokens (login/refresh, `azure` scope) — for the
 * production host (whatever the registry routes to) — in `x_zumo_auth_hosts`. Every other host a v2 call can end
 * up on (a registry row pointing elsewhere, a document `Location` on another
 * host, stage during tests) gets its token on demand from
 * `POST /v1/zumo-token`, which exchanges the session's Bearer for an EasyAuth
 * token of exactly that host — provided the host is on the server's
 * allow-list. The result is merged into the session's host map (persisted), so
 * a host is requested once per session and renewed through the same call when
 * MEDIS answers 401 (see `createZumoClient`).
 *
 * Failures are remembered per host so a hot path cannot hammer the auth
 * server: a host the server refuses (422 not allow-listed, 403 no `azure`
 * scope) is "not issuable" for 10 minutes — nothing retries it, not even a
 * 401 — while a transient failure (503 EasyAuth, network) pauses the host for
 * 30 s. Everything is forgotten the moment the session ends (store watcher at
 * the bottom), so the next account can inherit neither a verdict nor a token
 * request that was still in flight.
 *
 * NOTE: imports `authApi` from `lib/api/client`, which imports this module —
 * safe only while nothing here touches an import at module-evaluation time.
 */

/** Wire shape of `POST /v1/zumo-token`. */
interface ZumoTokenDto {
  host: string;
  x_zumo_auth: string;
}

/** 422/403: the server will not issue for this host — nothing a retry can change soon. */
const NOT_ISSUABLE_TTL_MS = 10 * 60_000;
/** 503/network: EasyAuth or the auth server hiccuped — worth trying again shortly. */
const FAILURE_COOLDOWN_MS = 30_000;

interface HostMemo {
  reason: 'not-issuable' | 'failed';
  until: number;
  /** HTTP status of the failed attempt (undefined = no response). */
  status?: number;
}

/**
 * Statuses that mean the auth server itself could not serve `/v1/zumo-token`
 * (route missing, app error, bad gateway) while the token GRANT may still
 * work — so an OAuth refresh, which re-issues the default host's token, is a
 * sensible fallback. Not 503 (EasyAuth refused/unreachable: the grant's
 * exchange would fail the same way) and not 422/403 (a verdict).
 */
const REFRESH_FALLBACK_STATUSES = new Set([404, 405, 500, 502, 504]);

/** One request per host at a time: concurrent first calls to a new host share it. */
const inFlight = new Map<string, Promise<string | null>>();
const memos = new Map<string, HostMemo>();
/** Refresh fallbacks in flight / recently tried, per host (see `renewViaRefresh`). */
const refreshFallbacks = new Map<string, Promise<string | null>>();
const refreshMemos = new Map<string, number>();
/**
 * Bumped by `resetZumoTokenState()`. A request captures it when it starts and
 * discards its own result if the session changed meanwhile, so a token issued
 * for the previous account is never written into the next one's map.
 */
let generation = 0;

export interface RequestZumoTokenOptions {
  /**
   * Renew even when the map already holds a token for the host (it just got a
   * 401) and ignore a transient-failure memo. A "not issuable" verdict still
   * stands, and concurrent renewals still share one request.
   */
  force?: boolean;
}

/**
 * The `X-ZUMO-AUTH` token for the host of `url`, issued on demand when the
 * session's host map has no entry for that host. Legacy sessions (no map,
 * issued before the per-host model) get their single token without any lazy
 * call — the next OAuth refresh upgrades them. Never throws; null when there
 * is nothing to send (no url/session, unusable URL, host refused or unreachable).
 */
export async function ensureZumoToken(url: string | undefined): Promise<string | null> {
  if (!url) return null;
  const known = authSession.getZumoToken(url);
  // A legacy session (single pre-map token) has nothing to ask for: only the
  // OAuth refresh renews it. A session with neither a map nor a legacy token
  // (its grant exchange failed) is asked for like a per-host one.
  if (known || authSession.hasLegacyZumoOnly()) return known;
  const host = hostOf(url);
  return host ? requestZumoToken(host) : null;
}

/**
 * Whether the last `/v1/zumo-token` failure for `host` was the auth server's
 * own (route missing / app error), i.e. a case where the OAuth refresh — which
 * re-issues the default host's token through the grant — may still succeed.
 */
export function canRenewViaRefresh(host: string): boolean {
  const memo = memos.get(host.toLowerCase());
  return Boolean(
    memo && memo.reason === 'failed' && memo.status !== undefined && REFRESH_FALLBACK_STATUSES.has(memo.status),
  );
}

/**
 * Last-resort renewal for a host whose token MEDIS rejected and that
 * `/v1/zumo-token` could not re-issue because the endpoint is missing or
 * broken (see `canRenewViaRefresh`): one OAuth refresh (single-flight across
 * the app), whose grant brings a fresh token for the default host. At most one
 * attempt per host per cooldown, so a persistently broken endpoint costs one
 * refresh per 30 s, not one per 401. Resolves the host's token afterwards, or null.
 */
export function renewViaRefresh(host: string): Promise<string | null> {
  const key = host.toLowerCase();
  const until = refreshMemos.get(key);
  if (until && until > Date.now()) return Promise.resolve(null);
  const pending = refreshFallbacks.get(key);
  if (pending) return pending;

  refreshMemos.set(key, Date.now() + FAILURE_COOLDOWN_MS);
  const startedIn = generation;
  const attempt = (async () => {
    const refreshed = await refreshTokens();
    if (!refreshed || startedIn !== generation) return null;
    return authSession.getZumoHostToken(key);
  })().finally(() => {
    if (refreshFallbacks.get(key) === attempt) refreshFallbacks.delete(key);
  });
  refreshFallbacks.set(key, attempt);
  return attempt;
}

/**
 * Asks the auth server for the EasyAuth token of `host` (hostname, no port)
 * and merges it into the session's host map. Single-flight per host; honours
 * the host's existing token and the failure memos unless `force` (see
 * `RequestZumoTokenOptions`). Resolves null on failure — callers send nothing
 * and let MEDIS answer.
 */
export function requestZumoToken(
  host: string,
  { force = false }: RequestZumoTokenOptions = {},
): Promise<string | null> {
  const key = host.toLowerCase();
  if (!authSession.isAuthenticated()) return Promise.resolve(null);

  if (!force) {
    const known = authSession.getZumoHostToken(key);
    if (known) return Promise.resolve(known);
  }

  const memo = memos.get(key);
  if (memo) {
    if (memo.until <= Date.now()) memos.delete(key);
    else if (memo.reason === 'not-issuable' || !force) return Promise.resolve(null);
  }

  const pending = inFlight.get(key);
  if (pending) return pending;

  const request = issue(key, generation).finally(() => {
    // Only forget our own entry: a reset may have replaced it meanwhile.
    if (inFlight.get(key) === request) inFlight.delete(key);
  });
  inFlight.set(key, request);
  return request;
}

async function issue(host: string, startedIn: number): Promise<string | null> {
  try {
    // Through `authApi`: Bearer attached, JWT refreshed + replayed on a 401.
    const { data } = await authApi.post<ZumoTokenDto>(endpoints.auth.zumoToken, { host });
    if (startedIn !== generation) return null; // the session ended while we waited

    const token = typeof data?.x_zumo_auth === 'string' ? data.x_zumo_auth : '';
    const issuedFor = typeof data?.host === 'string' ? data.host.toLowerCase() : host;
    // A token is bound to its host: never file one under a host it was not issued for.
    if (!token || issuedFor !== host) {
      remember(host, 'failed');
      // Never the body: a mismatched response may still carry a live token.
      if (env.isDev) {
        console.warn('[zumo] unusable token response for', host, { issuedFor, hasToken: Boolean(token) });
      }
      return null;
    }
    authSession.setZumoHostToken(host, token);
    return token;
  } catch (error) {
    if (startedIn !== generation) return null;
    const status = axios.isAxiosError(error) ? error.response?.status : undefined;
    if (status === 422 || status === 403) {
      remember(host, 'not-issuable');
      if (env.isDev) {
        const why =
          status === 422 ? 'host not on the auth server allow-list' : 'session lacks the azure scope';
        console.warn(`[zumo] no X-ZUMO-AUTH token issuable for ${host} (${status}: ${why})`);
      }
    } else {
      remember(host, 'failed', status);
      if (env.isDev) {
        console.warn(`[zumo] token request for ${host} failed (${status ?? 'network'}), retry in 30 s`);
      }
    }
    return null;
  }
}

function remember(host: string, reason: HostMemo['reason'], status?: number): void {
  const ttl = reason === 'not-issuable' ? NOT_ISSUABLE_TTL_MS : FAILURE_COOLDOWN_MS;
  memos.set(host, { reason, until: Date.now() + ttl, status });
}

/**
 * Forget every memo and in-flight request (their results are dropped). Runs
 * whenever the session ends; the dev "Reset sesiune ZUMO" calls it too, so a
 * host refused earlier is asked again after a server-side allow-list change.
 */
export function resetZumoTokenState(): void {
  generation += 1;
  inFlight.clear();
  memos.clear();
  refreshFallbacks.clear();
  refreshMemos.clear();
}

// The lazy state belongs to one session: drop it when the tokens go away
// (logout, failed refresh, a delegated-session start wiping the previous
// account) so the next account starts clean. Logins always `setTokens` a fresh
// bundle and only ever follow a cleared session or a fresh page load.
useAuthStore.subscribe((state, previous) => {
  if (!state.tokens && previous.tokens) resetZumoTokenState();
});
